import { useTranslation } from 'react-i18next';
import type { ClinicalResult } from '@/types/product';

interface ClinicalResultsChartProps {
  results: ClinicalResult[];
}

export default function ClinicalResultsChart({ results }: ClinicalResultsChartProps) {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#E8E8E6]">
      <div className="flex flex-col gap-1 mb-6">
        <h3 className="text-sm tracking-[0.12em] uppercase font-medium text-[#2A2A2A]">
          {t('products.clinicalResults')}
        </h3>
        <p className="text-[11px] text-[#8A8A8A] font-light">
          {t('products.clinicalSubtitle')}
        </p>
      </div>

      <div className="flex flex-col gap-6">
        {results.map((r, i) => (
          <div key={i} className="flex flex-col gap-3">
            <span className="text-xs font-medium text-[#2A2A2A]">{r.metric}</span>

            {/* Week 4 bar */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between text-[10px] text-[#8A8A8A]">
                <span>{t('products.week4')}</span>
                <span className={`font-medium ${r.week4 < 0 ? 'text-[#7B1113]' : 'text-[#2D6A4F]'}`}>
                  {r.week4 > 0 ? '+' : ''}{r.week4}{r.unit}
                </span>
              </div>
              <div className="w-full h-2 rounded-full bg-[#F0EFED] overflow-hidden">
                <div
                  className={`h-full rounded-full ${r.week4 < 0 ? 'bg-[#7B1113]' : 'bg-[#2D6A4F]'}`}
                  style={{ width: `${Math.min(Math.abs(r.week4) * 1.5, 100)}%` }}
                />
              </div>
            </div>

            {/* Week 8 bar */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between text-[10px] text-[#8A8A8A]">
                <span>{t('products.week8')}</span>
                <span className={`font-medium ${r.week8 < 0 ? 'text-[#7B1113]' : 'text-[#2D6A4F]'}`}>
                  {r.week8 > 0 ? '+' : ''}{r.week8}{r.unit}
                </span>
              </div>
              <div className="w-full h-2 rounded-full bg-[#F0EFED] overflow-hidden">
                <div
                  className={`h-full rounded-full ${r.week8 < 0 ? 'bg-[#7B1113]' : 'bg-[#2D6A4F]'}`}
                  style={{ width: `${Math.min(Math.abs(r.week8) * 1.5, 100)}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <p className="mt-6 text-[10px] text-[#8A8A8A] font-light italic">
        {t('products.clinicalDisclaimer')}
      </p>
    </div>
  );
}